/**
 * Period Configuration
 *
 * Defines scoring periods per sport for Squares contests.
 */

export interface PeriodConfig {
  periodCode: string;
  label: string;
  displayOrder: number;
  endCondition: string;
  prizeAmount: number;
}

/** Default period configurations keyed by uppercase sport code */
export const PERIOD_MAPPING: Record<string, PeriodConfig[]> = {
  NBA: [
    { periodCode: 'Q1', label: 'Q1', displayOrder: 1, endCondition: 'end_q1', prizeAmount: 0 },
    { periodCode: 'Q2', label: 'Q2', displayOrder: 2, endCondition: 'end_q2', prizeAmount: 0 },
    { periodCode: 'Q3', label: 'Q3', displayOrder: 3, endCondition: 'end_q3', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 4, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  NFL: [
    { periodCode: 'Q1', label: 'Q1', displayOrder: 1, endCondition: 'end_q1', prizeAmount: 0 },
    { periodCode: 'Q2', label: 'Q2', displayOrder: 2, endCondition: 'end_q2', prizeAmount: 0 },
    { periodCode: 'Q3', label: 'Q3', displayOrder: 3, endCondition: 'end_q3', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 4, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  NHL: [
    { periodCode: 'P1', label: 'Period 1', displayOrder: 1, endCondition: 'end_p1', prizeAmount: 0 },
    { periodCode: 'P2', label: 'Period 2', displayOrder: 2, endCondition: 'end_p2', prizeAmount: 0 },
    { periodCode: 'P3', label: 'Period 3', displayOrder: 3, endCondition: 'end_p3', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 4, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  NCAAMB: [
    { periodCode: 'HT', label: 'Halftime', displayOrder: 1, endCondition: 'halftime', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 2, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  // College football plays 4 quarters, same structure as the NFL.
  NCAAFB: [
    { periodCode: 'Q1', label: 'Q1', displayOrder: 1, endCondition: 'end_q1', prizeAmount: 0 },
    { periodCode: 'Q2', label: 'Q2', displayOrder: 2, endCondition: 'end_q2', prizeAmount: 0 },
    { periodCode: 'Q3', label: 'Q3', displayOrder: 3, endCondition: 'end_q3', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 4, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  MLB: [
    { periodCode: 'I1', label: 'Inning 1', displayOrder: 1, endCondition: 'end_i1', prizeAmount: 0 },
    { periodCode: 'I2', label: 'Inning 2', displayOrder: 2, endCondition: 'end_i2', prizeAmount: 0 },
    { periodCode: 'I3', label: 'Inning 3', displayOrder: 3, endCondition: 'end_i3', prizeAmount: 0 },
    { periodCode: 'I4', label: 'Inning 4', displayOrder: 4, endCondition: 'end_i4', prizeAmount: 0 },
    { periodCode: 'I5', label: 'Inning 5', displayOrder: 5, endCondition: 'end_i5', prizeAmount: 0 },
    { periodCode: 'I6', label: 'Inning 6', displayOrder: 6, endCondition: 'end_i6', prizeAmount: 0 },
    { periodCode: 'I7', label: 'Inning 7', displayOrder: 7, endCondition: 'end_i7', prizeAmount: 0 },
    { periodCode: 'I8', label: 'Inning 8', displayOrder: 8, endCondition: 'end_i8', prizeAmount: 0 },
    { periodCode: 'I9', label: 'Inning 9', displayOrder: 9, endCondition: 'end_i9', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Final', displayOrder: 10, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
  // Soccer: HT (cumulative score at half-time) + FINAL (full-time score,
  // incl. ET/pens for knockout games). Pattern matches every other sport
  // here — the last sub-period (H2) is rolled into FINAL so the same
  // square cannot win twice on a league fixture.
  SOCCER: [
    { periodCode: 'HT', label: 'Halftime', displayOrder: 1, endCondition: 'halftime', prizeAmount: 0 },
    { periodCode: 'FINAL', label: 'Full Time', displayOrder: 2, endCondition: 'end_of_game', prizeAmount: 0 },
  ],
};

/**
 * Get period configurations for a sport.
 * @param sport - Uppercase sport code (e.g. 'NFL', 'NBA')
 * @param customPeriods - Optional override; returned as-is if provided
 * @returns Array of period configs, or empty array if sport not found
 */
export function getPeriodsForSport(sport: string, customPeriods?: PeriodConfig[]): PeriodConfig[] {
  if (customPeriods && customPeriods.length > 0) return customPeriods;
  return PERIOD_MAPPING[sport.toUpperCase()] || [];
}
